import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Reveal, Overline, EASE } from "@/components/motion";
import ProjectCard from "@/components/ProjectCard";

const CATEGORIES = [
  "All",
  "Proposals & Decks",
  "Publications",
  "Digital & Social",
  "Motion",
];

export default function Work() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const visible = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  return (
    <div className="pt-16" data-testid="work-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-16 sm:pt-24 pb-12">
        <Reveal>
          <Overline>Portfolio</Overline>
          <h1 className="mt-4 font-serif text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05]">
            Work that made the complex <span className="italic text-accent">credible</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-foreground/70 leading-relaxed">
            Brand systems, pursuits, publications and digital experiences for consulting
            and enterprise teams.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pb-24 sm:pb-32">
        <Reveal className="flex flex-wrap gap-2 mb-14" delay={0.1}>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`work-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-mono rounded-full border transition-colors duration-300 ${
                filter === c
                  ? "bg-accent text-accent-foreground border-accent"
                  : "border-border text-muted-foreground hover:border-accent hover:text-accent"
              }`}
            >
              {c}
            </button>
          ))}
        </Reveal>

        {loading ? (
          <p className="text-muted-foreground font-mono text-sm" data-testid="work-loading">Loading…</p>
        ) : (
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16">
            <AnimatePresence mode="popLayout">
              {visible.map((p, i) => (
                <motion.div
                  key={p.id}
                  layout
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className={i % 2 === 1 ? "md:mt-20" : ""}
                >
                  <ProjectCard project={p} index={i} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
        {!loading && visible.length === 0 && (
          <p className="text-muted-foreground" data-testid="work-empty">No projects in this category yet.</p>
        )}
      </section>
    </div>
  );
}
